import { ArrowRightIcon, CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import LoadingSpinner from 'components/common/loading-spinner';
import { useStepAccordionActiveMaxHeight } from 'hooks/use-step-accordion-active-max-height';
import { useRef } from 'react';
import { SidePanelTab } from 'utils/side-panel-tabs';
import {
  getAccordionCircleClasses,
  getAccordionCollapsedRowClasses,
  StepActionButtonConfig
} from 'utils/step-sidebar-helpers';
import Tooltip from './tooltip';

type StepAccordionProps = {
  readonly tabs: SidePanelTab[];
  readonly activeTabId?: string;
  readonly onTabSelect?: (tabId: string) => void;
  readonly nextButton?: StepActionButtonConfig;
  readonly alternateNextButton?: StepActionButtonConfig;
  readonly readOnly?: boolean;
};

type StepCircleProps = {
  readonly stepNumber: number;
  readonly isComplete: boolean;
  readonly isActive: boolean;
  readonly isEnabled: boolean;
};

function StepCircle({
  stepNumber,
  isComplete,
  isActive,
  isEnabled
}: StepCircleProps) {
  return (
    <span
      className={clsx(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
        getAccordionCircleClasses(isComplete, isActive, isEnabled)
      )}
    >
      {isComplete ? <CheckIcon className='h-4 w-4' /> : stepNumber}
    </span>
  );
}

type ActionButtonProps = {
  readonly config: StepActionButtonConfig;
  readonly variant: 'solid' | 'outline';
  readonly showArrow?: boolean;
};

function ActionButton({
  config,
  variant,
  showArrow = false
}: ActionButtonProps) {
  return (
    <div className='relative'>
      <button
        type='button'
        onClick={config.onClick}
        disabled={config.disabled}
        className={clsx(
          'flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold transition-colors',
          variant === 'solid' && 'bg-green-600 text-white',
          variant === 'solid' && !config.disabled && 'hover:bg-green-700',
          variant === 'outline' &&
            'border border-green-600 bg-white text-green-700',
          variant === 'outline' && !config.disabled && 'hover:bg-green-50',
          config.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        )}
      >
        <span>{config.text}</span>
        {showArrow && <ArrowRightIcon className='h-4 w-4' />}
      </button>
      {config.disabled && config.disabledReason && (
        <Tooltip text={config.disabledReason} />
      )}
    </div>
  );
}

export default function StepAccordion({
  tabs,
  activeTabId,
  onTabSelect,
  nextButton,
  alternateNextButton,
  readOnly = false
}: StepAccordionProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const activeMaxHeight = useStepAccordionActiveMaxHeight(
    listRef,
    activeRef,
    `${activeTabId ?? ''}:${readOnly}:${tabs.map((tab) => tab.id).join(',')}`
  );

  return (
    <div ref={listRef} className='flex min-h-0 flex-1 flex-col gap-3'>
      {tabs.map((tab, index) => {
        const stepNumber = index + 1;
        const isActive = !readOnly && tab.id === activeTabId;
        const isComplete = tab.complete === true;
        const isEnabled = !tab.disabled;
        const isInteractive = !readOnly && isEnabled;

        if (!isActive) {
          return (
            <button
              key={tab.id}
              id={`step-accordion-${tab.id}`}
              type='button'
              disabled={!isInteractive}
              onClick={() => isInteractive && onTabSelect?.(tab.id)}
              className={clsx(
                'flex w-full shrink-0 items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left transition-colors',
                getAccordionCollapsedRowClasses(isInteractive, readOnly)
              )}
            >
              <StepCircle
                stepNumber={stepNumber}
                isComplete={isComplete}
                isActive={false}
                isEnabled={isEnabled}
              />
              <span
                className={clsx(
                  'min-w-0 flex-1 truncate text-base font-semibold',
                  isEnabled ? 'text-gray-500' : 'text-gray-400'
                )}
              >
                {tab.label}
              </span>
              {tab.isLoading && (
                <LoadingSpinner className='h-3.5 w-3.5 shrink-0 animate-spin text-gray-400' />
              )}
              {tab.summary && (
                <span
                  className={clsx(
                    'shrink-0 text-sm font-medium',
                    isComplete ? 'text-green-700' : 'text-gray-400'
                  )}
                >
                  {tab.summary}
                </span>
              )}
            </button>
          );
        }

        return (
          <div
            key={tab.id}
            ref={activeRef}
            id={`step-accordion-${tab.id}`}
            className='flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'
            style={
              activeMaxHeight !== undefined
                ? { maxHeight: activeMaxHeight }
                : undefined
            }
          >
            <div className='flex shrink-0 items-center gap-3 border-b border-gray-100 px-4 py-3'>
              <StepCircle
                stepNumber={stepNumber}
                isComplete={isComplete}
                isActive
                isEnabled
              />
              <span className='min-w-0 flex-1 truncate text-base font-semibold text-gray-900'>
                {tab.label}
              </span>
              {tab.isLoading && (
                <LoadingSpinner className='h-3.5 w-3.5 shrink-0 animate-spin text-gray-400' />
              )}
              {tab.summary && (
                <span
                  className={clsx(
                    'shrink-0 text-sm font-medium',
                    isComplete ? 'text-green-700' : 'text-gray-500'
                  )}
                >
                  {tab.summary}
                </span>
              )}
            </div>
            <div className='relative min-h-0 overflow-y-auto'>
              {tab.renderContent()}
              {tab.isLoading && (
                <div className='absolute inset-0 flex items-center justify-center bg-white/70'>
                  <LoadingSpinner className='h-6 w-6 animate-spin text-gray-500' />
                </div>
              )}
            </div>
            {(nextButton || alternateNextButton) && (
              <div className='flex shrink-0 flex-col gap-2 px-3 pb-3 pt-3'>
                {nextButton && (
                  <ActionButton config={nextButton} variant='solid' showArrow />
                )}
                {alternateNextButton && (
                  <ActionButton
                    config={alternateNextButton}
                    variant='outline'
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
